// lib/provenance/signImageExport.ts
type SignableFormat = 'image/jpeg' | 'image/png' | 'image/webp' | 'image/tiff';
type Slot = { hashed: BlobPart[]; offset: number; length: number; assemble: (manifest: Uint8Array<ArrayBuffer>) => BlobPart[] };

const placeholders = new Map<SignableFormat, Promise<Uint8Array<ArrayBuffer>>>();

const request = async (body: Record<string, unknown>): Promise<Uint8Array<ArrayBuffer>> => {
  const response = await fetch('/api/provenance/c2pa', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!response.ok) throw new Error(`signing service answered ${response.status}`);
  const { bytes } = await response.json() as { bytes: string };
  return Uint8Array.from(atob(bytes), (char) => char.charCodeAt(0));
};

const placeholder = (format: SignableFormat) => {
  const cached = placeholders.get(format);
  if (cached) return cached;
  const pending = request({ action: 'placeholder', format });
  placeholders.set(format, pending);
  pending.catch(() => placeholders.delete(format));
  return pending;
};

const hasAscii = (bytes: Uint8Array, start: number, text: string) => [...text].every((char, i) => bytes[start + i] === char.charCodeAt(0));

const detectFormat = (head: Uint8Array): SignableFormat => {
  if (head[0] === 0xff && head[1] === 0xd8) return 'image/jpeg';
  if (hasAscii(head, 0, '\x89PNG\r\n\x1a\n')) return 'image/png';
  if (hasAscii(head, 0, 'RIFF') && hasAscii(head, 8, 'WEBP')) return 'image/webp';
  if (hasAscii(head, 0, 'II*\0')) return 'image/tiff';
  throw new Error(hasAscii(head, 0, 'MM\0*') ? 'big-endian TIFF' : 'unsupported format');
};

const read = async (blob: Blob, start: number, length: number) => new Uint8Array(await blob.slice(start, start + length).arrayBuffer());

const readTiffIfd0 = async (blob: Blob, head: Uint8Array<ArrayBuffer>) => {
  const ifd0 = new DataView(head.buffer).getUint32(4, true);
  const count = new DataView((await read(blob, ifd0, 2)).buffer).getUint16(0, true);
  const entries = await read(blob, ifd0 + 2, count * 12 + 4);
  if (entries.length !== count * 12 + 4) throw new Error('truncated TIFF directory');
  return { count, entries };
};

// A second manifest store beside the producer's makes the file unreadable, so a marked file is left as it is.
const carriesManifest = async (blob: Blob, format: SignableFormat, head: Uint8Array<ArrayBuffer>): Promise<boolean> => {
  if (format === 'image/jpeg') {
    for (let at = 2; at + 4 <= blob.size;) {
      const segment = await read(blob, at, 20);
      if (segment[0] !== 0xff || segment[1] === 0xda || segment[1] === 0xd9) return false;
      if (segment[1] === 0xff) { at += 1; continue; }
      // APP11 JUMBF: length, 'JP' common identifier, instance and sequence numbers, then the box header.
      if (segment[1] === 0xeb && hasAscii(segment, 4, 'JP') && hasAscii(segment, 16, 'jumb')) return true;
      at += 2 + (segment[2]! << 8 | segment[3]!);
    }
    return false;
  }
  if (format === 'image/png') {
    for (let at = 8; at + 8 <= blob.size;) {
      const chunk = await read(blob, at, 8);
      if (hasAscii(chunk, 4, 'caBX')) return true;
      if (hasAscii(chunk, 4, 'IDAT') || hasAscii(chunk, 4, 'IEND')) return false;
      at += 12 + new DataView(chunk.buffer).getUint32(0);
    }
    return false;
  }
  if (format === 'image/webp') {
    for (let at = 12; at + 8 <= blob.size;) {
      const chunk = await read(blob, at, 8);
      if (hasAscii(chunk, 0, 'C2PA')) return true;
      const size = new DataView(chunk.buffer).getUint32(4, true);
      at += 8 + size + (size % 2);
    }
    return false;
  }
  const { count, entries } = await readTiffIfd0(blob, head);
  return Array.from({ length: count }, (_, i) => entries[i * 12]! | entries[i * 12 + 1]! << 8).includes(0xcd41);
};

const inserted = (blob: Blob, offset: number, length: number): Slot => ({
  hashed: [blob],
  offset,
  length,
  assemble: (manifest) => [blob.slice(0, offset), manifest, blob.slice(offset)],
});

// The offsets and the exclusion each format gets are the ones c2pa-rs locates when it reads the file back.
const reserveSlot = async (blob: Blob, format: SignableFormat, head: Uint8Array<ArrayBuffer>, slotLength: number): Promise<Slot> => {
  const view = new DataView(head.buffer);
  if (format === 'image/jpeg') return inserted(blob, 2, slotLength);
  if (format === 'image/png') {
    if (!hasAscii(head, 12, 'IHDR')) throw new Error('PNG without a leading IHDR');
    return inserted(blob, 16 + view.getUint32(8) + 4, slotLength);
  }
  if (format === 'image/webp') {
    const pad = new Uint8Array(slotLength % 2);
    const riffSize = blob.size + slotLength + pad.length;
    if (riffSize > 0xffffffff) throw new Error('WebP too large for its RIFF size field');
    const header = head.slice(0, 8);
    new DataView(header.buffer).setUint32(4, riffSize, true);
    const chunk = new Uint8Array(8);
    chunk.set([...'C2PA'].map((char) => char.charCodeAt(0)));
    new DataView(chunk.buffer).setUint32(4, slotLength, true);
    // c2pa-rs excludes the chunk header with the manifest but not the RIFF pad byte, which therefore stays hashed.
    return {
      hashed: [header, blob.slice(8), pad],
      offset: blob.size,
      length: 8 + slotLength,
      assemble: (manifest) => [header, blob.slice(8), chunk, manifest, pad],
    };
  }
  // TIFF: a new IFD0 copying the old entries plus tag 0xCD41 pointing at the manifest, both appended to the file.
  const { count, entries } = await readTiffIfd0(blob, head);
  const base = blob.size + (blob.size % 2);
  const ifdSize = 2 + (count + 1) * 12 + 4;
  const slotOffset = base + ifdSize;
  if (slotOffset + slotLength > 0xffffffff) throw new Error('TIFF too large for 32-bit offsets');
  const tag = new Uint8Array(12);
  const tagView = new DataView(tag.buffer);
  tagView.setUint16(0, 0xcd41, true);
  tagView.setUint16(2, 7, true);
  tagView.setUint32(4, slotLength, true);
  tagView.setUint32(8, slotOffset, true);
  const sorted = [...Array.from({ length: count }, (_, i) => entries.subarray(i * 12, i * 12 + 12)), tag]
    .sort((a, b) => (a[0]! | a[1]! << 8) - (b[0]! | b[1]! << 8));
  const ifd = new Uint8Array(ifdSize);
  const ifdView = new DataView(ifd.buffer);
  ifdView.setUint16(0, count + 1, true);
  sorted.forEach((entry, i) => ifd.set(entry, 2 + i * 12));
  ifd.set(entries.subarray(count * 12), 2 + (count + 1) * 12);
  const header = head.slice(0, 8);
  new DataView(header.buffer).setUint32(4, base, true);
  const align = new Uint8Array(base - blob.size);
  return {
    hashed: [header, blob.slice(8), align, ifd],
    offset: slotOffset,
    length: slotLength,
    assemble: (manifest) => [header, blob.slice(8), align, ifd, manifest],
  };
};

export async function signImageExport(blob: Blob): Promise<Blob> {
  let signing: SignableFormat | null = null;
  try {
    const head = await read(blob, 0, 16);
    const format = detectFormat(head);
    if (await carriesManifest(blob, format, head)) throw new Error('already carries a manifest store');
    const slotLength = (await placeholder(format)).length;
    const slot = await reserveSlot(blob, format, head, slotLength);
    // ponytail: one materialization of the hashed bytes; if 20K exports hit memory limits, hash Blob slices with a streaming SHA-256.
    const digest = await crypto.subtle.digest('SHA-256', await new Blob(slot.hashed).arrayBuffer());
    const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    signing = format;
    const manifest = await request({ action: 'sign', format, offset: slot.offset, length: slot.length, sha256 });
    if (manifest.length !== slotLength) throw new Error('signed manifest does not fit its slot');
    return new Blob(slot.assemble(manifest), { type: blob.type });
  } catch (error) {
    // The slot size moves with the signing certificate, and a refused sign is the only place the page learns it.
    if (signing) placeholders.delete(signing);
    console.warn('[provenance] Download left without content credentials:', error instanceof Error ? error.message : error);
    return blob;
  }
}
